/**
 * Constantes du flux d'appel commercial — côté front.
 * Source de vérité unique (miroir de server/constants/commercial-call.js).
 * Ces valeurs reflètent les enums Prisma (LeadPriority, VoicemailChoice,
 * CallDirection, UserTaskType, LossReasonCategory).
 */

// ── Température de lead ────────────────────────────────────────────────
export const LeadPriority = {
  FROID: 'froid',
  TIEDE: 'tiede',
  CHAUD: 'chaud',
  TRES_CHAUD: 'tres_chaud',
}

export const LEAD_PRIORITY_LABELS = {
  froid: 'Froid',
  tiede: 'Tiède',
  chaud: 'Chaud',
  tres_chaud: 'Très chaud',
}

export const LEAD_PRIORITY_ORDER = ['froid', 'tiede', 'chaud', 'tres_chaud']
export const LEAD_PRIORITY_WEIGHT = { froid: 1, tiede: 2, chaud: 3, tres_chaud: 4 }

export const LEAD_PRIORITY_OPTIONS = LEAD_PRIORITY_ORDER.map((value) => ({
  value,
  label: LEAD_PRIORITY_LABELS[value],
}))

/** Couleurs de badge (texte / fond) par température, cohérentes avec le design system. */
export const LEAD_PRIORITY_COLORS = {
  froid: { color: '#1e6fd8', bg: '#e7f0fd' },
  tiede: { color: '#b7791f', bg: '#fdf6e3' },
  chaud: { color: '#d9730d', bg: '#fdebd8' },
  tres_chaud: { color: '#d64545', bg: '#fbe4e4' },
}

export function formatLeadPriority(value) {
  return LEAD_PRIORITY_LABELS[value] || ''
}

// ── Choix répondeur (Ne répond pas) ───────────────────────────────────
export const VoicemailChoice = {
  LEFT_MESSAGE: 'left_message',
  NOT_LEFT: 'not_left',
  CANNOT_LEAVE: 'cannot_leave',
}

export const VOICEMAIL_LABELS = {
  left_message: "J'ai laissé un message vocal",
  not_left: "Je n'ai pas laissé de message vocal",
  cannot_leave: 'Je ne peux pas laisser un message vocal',
}

export const VOICEMAIL_OPTIONS = [
  { value: VoicemailChoice.LEFT_MESSAGE, label: VOICEMAIL_LABELS.left_message },
  { value: VoicemailChoice.NOT_LEFT, label: VOICEMAIL_LABELS.not_left },
  { value: VoicemailChoice.CANNOT_LEAVE, label: VOICEMAIL_LABELS.cannot_leave },
]

// ── Résultat d'appel (valeur persistée QuoteCall.result) ───────────────
// ÉCRIT par les composables/contrôleurs d'appel, RELU (contains/equals/includes)
// par l'automatisation J+7/J+30, les stats et calls.service. Source unique :
// ne jamais modifier une valeur sans mettre à jour tous les consommateurs.
export const CallResult = {
  ANSWERED: 'Décroché',
  NO_ANSWER: 'Ne répond pas',
  NOT_INTERESTED: 'Pas intéressé',
}

// ── Direction d'appel ──────────────────────────────────────────────────
export const CallDirection = {
  SORTANT: 'sortant',
  ENTRANT: 'entrant',
  VISIO: 'visio',
}

export const CALL_DIRECTION_LABELS = {
  sortant: 'Appel sortant',
  entrant: 'Appel entrant',
  visio: 'Visio-conférence',
}

// ── Type de tâche ──────────────────────────────────────────────────────
export const UserTaskType = {
  MANUAL: 'manual',
  RELANCE: 'relance',
  DEVIS_A_ENVOYER: 'devis_a_envoyer',
}

export const USER_TASK_TYPE_LABELS = {
  manual: 'Tâche',
  relance: 'Relance téléphonique',
  devis_a_envoyer: 'Devis à envoyer',
}

// ── Motifs de perte ("Pas intéressé") ──────────────────────────────────
export const LossReasonCategory = {
  BUDGET: 'budget',
  ORGANISATION: 'organisation',
  BESOIN: 'besoin',
  CONCURRENCE: 'concurrence',
  ADMINISTRATIF: 'administratif',
  DECISION: 'decision',
  COMMERCIAL: 'commercial',
}

export const LOSS_REASON_CATEGORY_LABELS = {
  budget: 'Budget',
  organisation: 'Organisation',
  besoin: 'Besoin',
  concurrence: 'Concurrence',
  administratif: 'Administratif',
  decision: 'Décision',
  commercial: 'Commercial',
}

export const LOSS_REASON_CATEGORY_ORDER = [
  'budget',
  'organisation',
  'besoin',
  'concurrence',
  'administratif',
  'decision',
  'commercial',
]

// ── Relance téléphonique ───────────────────────────────────────────────
export const RELANCE_DEFAULT_OBJET = 'Relance téléphonique'

export const RELANCE_MOTIF_SUGGESTIONS = [
  'Attente retour devis',
  'Validation budget',
  "Choix d'une date",
  'Signature convention',
  'Relance générale',
]

// ── Provenance d'une note d'appel ─────────────────────────────────────
// Détermine la mention affichée devant le commentaire déposé sur la fiche.
export const CallCommentaireSource = {
  APPEL: 'appel',
  ANALYSE_BESOIN: 'analyse_besoin',
}

export const CALL_COMMENTAIRE_SOURCE_VALUES = Object.values(CallCommentaireSource)
